import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { EToastVariant, useToast } from '@/hooks/useToast';
import {
  IWishlist,
  IWishlistCreateRequest,
  IWishlistCreateResponse,
  IWishlistResponse,
  useGetWishlists,
} from '@/hooks/useWishlists';
import { IFavorite } from '@/redux/modules/wishlists';
import { getAuthenticatedUser, getIsAuthenticated } from '@/redux/selectors/auth/user';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { getQuoteEventData } from '@/redux/selectors/listing/bill';
import {
  trackListingFavoritedEvent,
  trackListingUnfavoritedEvent,
} from '@/services/analytics/listings';
import { EListingSource, IWishlistingEventData } from '@/services/analytics/listings/types';
import { trackWishlistCreatedEvent } from '@/services/analytics/wishlists';
import apiRequest from '@/services/apiRequest';
import { trackEvent } from '@/services/track-event';
import { ICampsiteCategory } from '@/services/types/search/campgrounds/id';
import { ERentalCategory, IListingData } from '@/services/types/search/rentals/id';
import { getCoreApi } from '@/utility/getCoreApi';
import { ICampgroundTile, IRentalTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';
import { getItemFromSessionStorage, setItemInSessionStorage } from '@/utility/sessionStorage';
import { triggerLoginModal } from '@/utility/triggerLoginModal';
import { EWishlistRoles } from '@/utility/wishlists';

import { WishlistModal } from './WishlistModal';
import { WishlistToast } from './WishlistToast';

type TTriggerComponentProps = {
  isFavorite: boolean | undefined;
  toggleFavorite: () => void;
};

type TWishlistModalWrapperProps = {
  isFavorite: boolean | undefined;
  item: ISearchResultTile | ICampsiteCategory | IListingData;
  wishlistingEventData?: IWishlistingEventData;
  TriggerComponent: (props: TTriggerComponentProps) => React.ReactNode;
  isListingPage?: boolean;
  customWishlistName?: string;
  itemType?: 'rental' | 'campground' | 'campsite';
};

export const WishlistModalWrapper = ({
  isFavorite,
  item,
  wishlistingEventData,
  TriggerComponent,
  isListingPage,
  customWishlistName,
  itemType,
}: TWishlistModalWrapperProps) => {
  const intl = useIntl();
  const { showToast, showCustomToast, closeToast } = useToast();
  const userFavorites = useSelector(getUserFavorites);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const userData = useSelector(getAuthenticatedUser);
  const quoteEventData = useSelector(getQuoteEventData);
  const isOutdoorsyStay = quoteEventData?.isOutdoorsyStay;

  const rentalCategory = ERentalCategory.RV;

  const { data: wishlists, mutate: refetchWishlists } = useGetWishlists({
    isAuthenticated,
    examplesOnly: false,
    excludeExamples: true,
    rentalCategory,
  });
  const [isOpen, setIsOpen] = useState(false);

  const isCampground =
    itemType === 'campground' ||
    (itemType !== 'campsite' && 'campground' in item && !!(item as ICampgroundTile).campground);

  const itemId = isCampground
    ? (item as ICampsiteCategory).campground_id || item.id
    : (item as ICampsiteCategory).rental_id || item.id;

  const rentalType = isCampground ? 'campground' : 'RV';

  const [isLoading, setIsLoading] = useState(false);
  const [toastFavorite, setToastFavorite] = useState<IFavorite | null>(null);

  const showGenericErrorToast = () => {
    showToast(
      EToastVariant.Error,
      intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
      intl.formatMessage({
        defaultMessage: 'Something went wrong, please try again.',
        id: '8GvVWZ',
      }),
    );
  };

  const handleClose = () => {
    setIsLoading(false);
    setIsOpen(false);
    setToastFavorite(null);
  };

  const handleShowCustomToast = (
    res: IWishlistResponse,
    wishlistId: number,
    wishlistItemCount: number,
    toastItemId?: number | null,
    creatorId?: number,
  ) => {
    showCustomToast(
      <WishlistToast
        selectedWishlistId={wishlistId}
        coverImageUrl={res.cover_image_url}
        wishlistTitle={res.title}
        itemId={toastItemId}
        wishlistItemCount={wishlistItemCount}
        creatorId={creatorId}
        rentalCategory={rentalCategory}
        onChangeWishlist={handleChangeStarted}
      />,
    );
  };

  const handleRemoveItemFromWishlist = async () => {
    const wishlistedRentalData =
      userFavorites.rentals.find(r => r?.item_id === itemId) ||
      userFavorites.campgrounds?.find(r => r?.item_id === itemId);

    if (!isAuthenticated || !wishlistedRentalData) {
      return null;
    }

    const currentWishlistToRemoveFrom = wishlists?.find(w => w.id === wishlistedRentalData?.id);

    if (!currentWishlistToRemoveFrom) {
      return null;
    }

    const requestUrl = `${getCoreApi()}/wishlists/${currentWishlistToRemoveFrom?.id}/remove`;
    const requestData = isCampground ? { campground_id: itemId } : { rental_id: itemId };

    try {
      await apiRequest(
        {
          url: requestUrl,
          method: 'POST',
          data: requestData,
        },
        true,
      );

      if (wishlistingEventData?.eventSource && currentWishlistToRemoveFrom) {
        const wishlistItemCount =
          currentWishlistToRemoveFrom.rental_count +
          currentWishlistToRemoveFrom.campground_count -
          1;
        const listingFavoriteCount =
          (item as ISearchResultTile).favoriteCount || (item as IListingData).favorite_count || 1;
        trackListingUnfavoritedEvent(
          item as IRentalTile,
          wishlistingEventData.index ? wishlistingEventData.index + 1 : 1,
          wishlistingEventData.eventSource,
          isOutdoorsyStay,
          wishlistingEventData?.page,
          undefined,
          currentWishlistToRemoveFrom.id,
          currentWishlistToRemoveFrom.title,
          wishlistItemCount,
          listingFavoriteCount - 1,
          wishlistedRentalData?.wishlist_creator_id,
        );
      }
      await refetchWishlists();

      handleClose();
    } catch {
      showGenericErrorToast();
      setSelectedWishlist(null);
      handleClose();
    }
  };

  const handleCreate = async (wishlistName: string) => {
    if (!itemId) {
      return;
    }

    setIsLoading(true);

    const requestData: IWishlistCreateRequest = {
      title: wishlistName,
      rental_category: rentalCategory,
    };

    if (isCampground) {
      requestData.campground_id = itemId;
    } else {
      requestData.rental_id = itemId;
    }

    try {
      const response = await apiRequest<IWishlistCreateResponse>(
        {
          url: `${getCoreApi()}/wishlists`,
          method: 'POST',
          data: requestData,
        },
        true,
      );

      const newWishlistItemCount = 1;
      handleShowCustomToast(response, response.id, newWishlistItemCount, itemId, userData?.id);

      trackWishlistCreatedEvent({
        rentalID: itemId,
        wishlistName: wishlistName,
        wishlistID: response.id,
        rentalCategory: rentalCategory,
      });

      const newWishlistId = response.id;
      const res = await refetchWishlists();

      // BE returns only id in the initial res when creating a new wishlist
      // we will need to find the new wishlist after the refetch
      const newWishlist = res?.find(w => w.id === newWishlistId);
      if (newWishlist) {
        setSelectedWishlist(newWishlist);
      }

      if (wishlistingEventData?.eventSource) {
        const listingFavoriteCount =
          (item as ISearchResultTile).favoriteCount || (item as IListingData).favorite_count || 0;
        trackListingFavoritedEvent(
          item as IRentalTile,
          wishlistingEventData.index ? wishlistingEventData.index + 1 : 1,
          wishlistingEventData.eventSource,
          isOutdoorsyStay,
          wishlistingEventData?.page,
          undefined,
          response.id,
          wishlistName,
          newWishlistItemCount,
          listingFavoriteCount + 1,
          userData?.id,
        );
      }
    } catch {
      showGenericErrorToast();
    } finally {
      handleClose();
    }
  };

  const handleAddToWishlist = async (clickedWishlist: IWishlist | null) => {
    const selectedWishlist = getSelectedWishlist();
    if ((!clickedWishlist && !selectedWishlist) || isFavorite || !itemId) {
      // prevent adding to wishlist if the item is already in the user's favorites
      // or no wishlist is selected
      return;
    }

    setIsLoading(true);

    if (clickedWishlist) {
      setSelectedWishlist(clickedWishlist);
    }

    const wishlistId = clickedWishlist?.id || selectedWishlist?.id;
    if (!wishlistId) {
      return;
    }

    const requestData = isCampground ? { campground_id: itemId } : { rental_id: itemId };

    try {
      const res = await apiRequest<IWishlistResponse>(
        {
          url: `${getCoreApi()}/wishlists/${wishlistId}/add`,
          method: 'PUT',
          data: requestData,
        },
        true,
      );

      const wishlist = selectedWishlist || clickedWishlist;
      const creatorId = wishlist?.user_details?.find(u => u.user_role === EWishlistRoles.Owner)?.id;
      const newWishlistItemCount =
        (wishlist?.rental_count || 0) + (wishlist?.campground_count || 0) + 1;
      if (wishlist) {
        handleShowCustomToast(res, wishlist.id, newWishlistItemCount, itemId, creatorId);
      }

      if (wishlistingEventData?.eventSource) {
        const listingFavoriteCount =
          (item as ISearchResultTile).favoriteCount || (item as IListingData).favorite_count || 1;
        trackListingFavoritedEvent(
          item as IRentalTile,
          wishlistingEventData.index ? wishlistingEventData.index + 1 : 1,
          wishlistingEventData.eventSource,
          isOutdoorsyStay,
          wishlistingEventData?.page,
          undefined,
          wishlistId,
          wishlist?.title,
          newWishlistItemCount,
          listingFavoriteCount + 1,
          creatorId,
        );
      }
      await refetchWishlists();
    } catch (error: any) {
      if (error?.response?.status === 400) {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({ defaultMessage: 'Failed to add to this wishlist', id: 'FrBBzq' }),
          intl.formatMessage(
            {
              defaultMessage:
                'This {type} cannot be added to this wishlist. Please try a different wishlist.',
              id: 'UaXfTt',
            },
            { type: rentalType },
          ),
        );
        setIsLoading(false);
        setSelectedWishlist(null);
        return; // Keep modal open so user can try again
      } else {
        showGenericErrorToast();
        setSelectedWishlist(null);
        handleClose();
      }
    }
  };

  const handleChangeWishlist = async (clickedWishlist: IWishlist | null) => {
    if (!clickedWishlist || !selectedWishlist || !toastFavorite) {
      return;
    }

    setIsLoading(true);

    // The BE call will remove the rental from the current wishlist and add it to the newly selected one
    // afterwards set selectedWishlist to the clickedWishlist
    const oldWishlistId = selectedWishlist.id;
    const newWishlistId = clickedWishlist.id;

    if (oldWishlistId === newWishlistId) {
      showToast(
        EToastVariant.Alert,
        intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        intl.formatMessage({
          defaultMessage: 'The rental is already included in the selected wishlist.',
          id: 'LWTiz3',
        }),
      );
      handleClose();
      return;
    }

    const changeRequestData = isCampground
      ? {
          old_wishlist_id: oldWishlistId,
          new_wishlist_id: newWishlistId,
          campground_id: toastFavorite.item_id,
        }
      : {
          old_wishlist_id: oldWishlistId,
          new_wishlist_id: newWishlistId,
          rental_id: toastFavorite.item_id,
        };

    try {
      const response = await apiRequest<IWishlistResponse>(
        {
          url: `${getCoreApi()}/wishlists/change-wishlist`,
          method: 'POST',
          data: changeRequestData,
        },
        true,
      );

      const creatorId = clickedWishlist.user_details?.find(
        u => u.user_role === EWishlistRoles.Owner,
      )?.id;
      const newWishlistItemCount =
        clickedWishlist.rental_count + clickedWishlist.campground_count + 1;
      handleShowCustomToast(
        response,
        newWishlistId,
        newWishlistItemCount,
        toastFavorite.item_id,
        creatorId,
      );
      setSelectedWishlist(clickedWishlist);

      trackListingFavoritedEvent(
        item as IRentalTile,
        1,
        EListingSource.CHANGE_WISHLIST,
        isOutdoorsyStay,
        undefined,
        undefined,
        clickedWishlist.id,
        clickedWishlist.title,
        newWishlistItemCount,
        (item as ISearchResultTile).favoriteCount,
        creatorId,
      );

      await refetchWishlists();
    } catch (error: any) {
      if (error?.response?.status === 400) {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({ defaultMessage: 'Failed to add to this wishlist', id: 'FrBBzq' }),
          intl.formatMessage(
            {
              defaultMessage:
                'This {type} cannot be added to this wishlist. Please try a different wishlist.',
              id: 'UaXfTt',
            },
            { type: rentalType },
          ),
        );
        setIsLoading(false);
        setSelectedWishlist(null);
        return; // Keep modal open so user can try again
      } else {
        showGenericErrorToast();
        setSelectedWishlist(null);
        handleClose();
      }
    }
  };

  const toggleFavorite = () => {
    if (!isAuthenticated) {
      triggerLoginModal(0);
      return null;
    }

    if (isFavorite) {
      handleRemoveItemFromWishlist();
    } else {
      if (selectedWishlist) {
        handleAddToWishlist(null);
      } else {
        setIsOpen(true);
      }
    }

    if (isListingPage) {
      trackEvent({
        event: 'Listing : Save',
        action: 'save',
        rental_id: itemId,
      });
    } else {
      trackEvent({ event: 'rv-show/click', action: 'save', rentalId: itemId });
    }
  };

  const getSelectedWishlist = () => {
    const storedWishlistId = getItemFromSessionStorage('selectedWishlist');
    if (!storedWishlistId || !wishlists) return null;
    return wishlists.find(w => w.id.toString() === storedWishlistId);
  };

  const setSelectedWishlist = (wishlist: IWishlist | null) => {
    setItemInSessionStorage('selectedWishlist', wishlist?.id?.toString() || '');
  };

  const handleChangeStarted = (favorites: ReturnType<typeof getUserFavorites>) => {
    const foundFavorite =
      favorites.rentals.find(f => f.item_id === itemId) ||
      favorites.campgrounds.find(f => f.item_id === itemId);

    // close all active toasts to prevent multiple modal instances from being open
    closeToast();

    if (foundFavorite) {
      //remember the favorite to be possibly moved into a new wishlist
      // in case no favorite is found, only open the modal to wishlist the rental again
      // could happen if the user first removes from wishlist with the heart button and then clicks the toast
      setToastFavorite(foundFavorite);
    }

    setIsLoading(false);
    setIsOpen(true);
  };

  const selectedWishlist = getSelectedWishlist();

  return (
    <>
      <TriggerComponent isFavorite={isFavorite} toggleFavorite={toggleFavorite} />
      {isOpen && (
        // Render the modal inside a hidden div
        // and use it to prevent clicks from bubbling up to the tile
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div className="hidden" onClick={e => e.stopPropagation()}>
          <WishlistModal
            isOpen={isOpen}
            item={item}
            wishlists={wishlists}
            isLoading={isLoading}
            selectedWishlist={selectedWishlist}
            onClose={handleClose}
            onCreateWishlist={(wishlistName: string) => handleCreate(wishlistName)}
            onAddToWishlist={toastFavorite ? handleChangeWishlist : handleAddToWishlist}
            customWishlistName={customWishlistName}
          />
        </div>
      )}
    </>
  );
};
